import { logger, useDynamicTabsStorage, type TabProps } from '@talos/kyoko';
import { useCallback } from 'react';

interface ResolveTabLabelParams {
  /** The ID of the set of tabs you want to resolve within */
  tabSetID: string;
  /** The id of the specific tab you're looking for */
  tabID: string;
  /** The key of the "ID" property on the Tab object. Defaults to "id", but can be set to for example "OrderID", or whatever else */
  tabIDKey?: string;
  /**
   * The hook does tab label resolution on the set of currently persisted tabs. If you want to do resolution on the set of persisted tabs
   * plus some set of static (hard-coded) tabs, pass them here.
   */
  nonPersistedTabs?: TabProps[];
}

/**
 * This hook returns a function which helps you resolve tab ids to their labels.
 * It hooks up to the AppConfig state, as in the same place that usePersistedTabs connects to.
 * It resolves tab uuids -> labels by grabbing tabs from the AppConfig TabStorage, and also allows you to pass in
 * non-persisted tabs (in other words static hard coded tabs), which are also used to look within first.
 */
export const useTabLabelResolver = () => {
  const { getState } = useDynamicTabsStorage();

  const resolveTabLabel = useCallback(
    ({ tabSetID, tabID, tabIDKey = 'id', nonPersistedTabs }: ResolveTabLabelParams) => {
      if (nonPersistedTabs) {
        const maybeFoundTab = nonPersistedTabs.find(tab => tab[tabIDKey as keyof typeof tab] === tabID);
        if (maybeFoundTab) {
          return maybeFoundTab.label ?? maybeFoundTab[tabIDKey as keyof typeof maybeFoundTab];
        }
      }

      const tabsState = getState(tabSetID);

      if (!tabsState) {
        // If the tabID is not provided, we don't need to log an error
        if (tabID) {
          // Shouldn't happen, but it can happen if you reset the app config and then try to open a previous tab.
          logger.error(
            new Error(
              'Unable to resolve tab ID to label in useTabLabelResolver due to not finding any tabs the given tab set ID'
            ),
            {
              extra: {
                tabSetID,
                tabID,
              },
            }
          );
        }
        return undefined;
      }

      return tabsState.items.find(tab => tab[tabIDKey as keyof typeof tab] === tabID)?.label;
    },
    [getState]
  );

  return {
    resolveTabLabel,
  };
};
